import { useEffect, useState } from "react"

// Build-Metadaten des aktuell ausgelieferten Bundles. Wird zur Build-Zeit von
// update-build.js nach public/meta.json geschrieben (CRA kopiert es nach
// build/meta.json, ausgeliefert unter ${PUBLIC_URL}/meta.json) und hier zur
// Laufzeit einmalig geladen. Zweck: Rückverfolgbarkeit "welcher Commit läuft
// live?" für Support/Bug-Triage. Im Dev-Modus (kein Build) fehlt meta.json ggf.
// oder enthält nur ältere Felder — der Code degradiert dann sauber zu null.
export type BuildInfo = {
    version: string
    commit: string
    commitCount: number
    branch?: string
    buildDate: number
    buildDateIso: string
}

let cached: BuildInfo | null = null
let inflight: Promise<BuildInfo | null> | null = null
let configuredUrl: string | undefined

/** Nur der Embed-Einstieg setzt eine absolute Adresse (Bundle läuft auf der Kundenseite); iFrame nutzt den Default */
export function setBuildInfoUrl(url: string): void {
    configuredUrl = url
}

function metaUrl(): string {
    if (configuredUrl) return configuredUrl
    // PUBLIC_URL folgt der package.json-"homepage" (=/build in Production),
    // ist im Dev-Modus i.d.R. leer. Trailing Slash normalisieren.
    const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "")
    return `${base}/meta.json`
}

export async function loadBuildInfo(): Promise<BuildInfo | null> {
    if (cached) return cached
    if (inflight) return inflight

    // In Test-/SSR-Umgebungen (jsdom) fehlt fetch ggf. -> sauber zu null degradieren
    if (typeof fetch === "undefined") return null

    inflight = fetch(metaUrl(), { cache: "no-store" })
        .then((res) => (res.ok ? res.json() : null))
        .then((data: BuildInfo | null) => {
            cached = data
            return data
        })
        .catch(() => null)
        .finally(() => {
            inflight = null
        })

    return inflight
}

/**
 * React-Hook: liefert die Build-Metadaten (oder null, solange/falls nicht
 * verfügbar). Lädt meta.json genau einmal pro Session (Modul-Cache).
 */
export function useBuildInfo(): BuildInfo | null {
    const [info, setInfo] = useState<BuildInfo | null>(cached)

    useEffect(() => {
        if (cached) return
        let active = true
        loadBuildInfo().then((data) => {
            if (active) setInfo(data)
        })
        return () => {
            active = false
        }
    }, [])

    return info
}
